import { useEffect, useState } from "react";
import { api, fmtCurrency, DEFAULT_CURRENCY, DEFAULT_SYMBOL } from "@/lib/api";
import { useCurrencies, currencySymbol, sumByCurrency, sortedCurrencyEntries } from "@/lib/currency";
import { FileText, Pencil, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import QuotationBuilder from "@/components/QuotationBuilder";
import QuotationView from "@/components/QuotationView";
import Modal from "@/components/Modal";

export default function Quotations() {
  const currencies = useCurrencies();
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState("");
  const [editingQuote, setEditingQuote] = useState(null);
  const [viewingQuote, setViewingQuote] = useState(null);

  const load = () => api.get("/quotations").then((r) => setQuotes(r.data));
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this quotation?")) return;
    await api.delete(`/quotations/${id}`);
    toast.success("Quotation deleted");
    load();
  };

  const onEditSaved = () => {
    setEditingQuote(null);
    load();
  };

  const filtered = quotes.filter((q) =>
    [q.quote_no, q.customer_snapshot?.company, q.customer_snapshot?.contact].some((v) => v?.toLowerCase().includes(search.toLowerCase()))
  );

  const fobByCurrency = sumByCurrency(quotes, "fob_total");
  const cifByCurrency = sumByCurrency(quotes, "cif_total");
  const exworksByCurrency = sumByCurrency(quotes, "exworks_total");
  const countByCurrency = {};
  for (const q of quotes) {
    const code = q.currency || DEFAULT_CURRENCY;
    countByCurrency[code] = (countByCurrency[code] || 0) + 1;
  }
  const currencyTotals = sortedCurrencyEntries(fobByCurrency);

  return (
    <div data-testid="quotations-page">
      <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <div className="card-ledger" style={{ padding: 18 }} data-testid="quotes-kpi-total">
          <div className="overline" style={{ fontSize: 10 }}>Total Issued</div>
          <div className="font-mono-data" style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{quotes.length}</div>
        </div>
        {currencyTotals.map(([code, total]) => (
          <div key={code} className="card-ledger" style={{ padding: 18 }} data-testid={`quotes-kpi-${code}`}>
            <div className="overline" style={{ fontSize: 10 }}>{code} Totals</div>
            <div className="font-mono-data" style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color: "var(--primary)" }} data-testid={`quotes-kpi-fob-${code}`}>
              FOB {fmtCurrency(total, code, currencySymbol(code, currencies))}
            </div>
            <div className="font-mono-data" style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }} data-testid={`quotes-kpi-cif-${code}`}>
              CIF {fmtCurrency(cifByCurrency[code] || 0, code, currencySymbol(code, currencies))}
            </div>
            <div className="font-mono-data" style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }} data-testid={`quotes-kpi-exworks-${code}`}>
              Ex-Works {fmtCurrency(exworksByCurrency[code] || 0, code, currencySymbol(code, currencies))}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 6 }}>
              {countByCurrency[code]} quote{countByCurrency[code] === 1 ? "" : "s"}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 items-center mb-4">
        <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 420 }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-2)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by quote # or customer…" className="input-base" style={{ paddingLeft: 36 }} data-testid="quotations-search" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }} data-testid="quotes-empty">
          <FileText size={28} style={{ margin: "0 auto 12px", color: "var(--text-2)" }} />
          <div style={{ fontSize: 14 }}>No quotations yet. Create one from a customer profile.</div>
        </div>
      ) : (
        <div className="card-ledger overflow-hidden" data-testid="quotations-table">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--surface-2)" }}>
                {["Quote #", "Customer", "By", "Currency", "Items", "Date", "CIF", "FOB", "Ex-Works", ""].map((h, i) => (
                  <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 16px", textAlign: i >= 6 && i <= 8 ? "right" : "left", borderBottom: "1px solid var(--border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => (
                <tr key={q.id} style={{ borderBottom: "1px solid var(--border)" }} data-testid={`quote-row-${q.id}`}>
                  <td className="font-mono-data" style={{ padding: "14px 16px", fontSize: 13 }}>
                    <button
                      onClick={() => setViewingQuote(q)}
                      className="btn-ghost"
                      style={{ padding: 0, fontWeight: 600, color: "var(--primary)", textDecoration: "underline", textUnderlineOffset: 3 }}
                      data-testid={`quote-view-${q.id}`}
                    >
                      {q.quote_no}
                    </button>
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 13 }}>
                    <div style={{ fontWeight: 600 }}>{q.customer_snapshot?.company || "—"}</div>
                    <div style={{ fontSize: 11, color: "var(--text-2)" }}>{q.customer_snapshot?.contact}</div>
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 12, color: "var(--text-2)" }} data-testid={`quote-by-${q.id}`}>{q.created_by_name || "—"}</td>
                  <td style={{ padding: "14px 16px", fontSize: 12, color: "var(--text-2)" }} className="font-mono-data">{q.currency || DEFAULT_CURRENCY}</td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }}>{q.items?.length || 0}</td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }} className="font-mono-data">{q.date || new Date(q.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }} className="font-mono-data">
                    {q.currency_symbol || DEFAULT_SYMBOL}{Number(q.cif_total || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}
                  </td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }} className="font-mono-data" data-testid={`quote-total-${q.id}`}>
                    <span style={{ fontWeight: 700, color: "var(--primary)" }}>{q.currency_symbol || DEFAULT_SYMBOL}{Number(q.fob_total || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}</span>
                  </td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }} className="font-mono-data">
                    {q.currency_symbol || DEFAULT_SYMBOL}{Number(q.exworks_total || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}
                  </td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }}>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditingQuote(q)} className="btn-ghost" style={{ color: "var(--text-2)" }} title="Edit" data-testid={`quote-edit-${q.id}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => onDelete(q.id)} className="btn-ghost" style={{ color: "var(--danger)" }} title="Delete" data-testid={`quote-delete-${q.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewingQuote && (
        <Modal
          title={viewingQuote.quote_no}
          subtitle={viewingQuote.customer_snapshot?.company}
          onClose={() => setViewingQuote(null)}
          size="lg"
        >
          <QuotationView quote={viewingQuote} />
        </Modal>
      )}

      {editingQuote && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 50, overflowY: "auto" }} data-testid="edit-quote-modal">
          <div style={{ maxWidth: 900, margin: "40px auto", background: "var(--surface)", borderRadius: 12, padding: 32, position: "relative" }}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline" style={{ fontSize: 10 }}>Editing</div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{editingQuote.quote_no}</div>
              </div>
              <button onClick={() => setEditingQuote(null)} className="btn-ghost" data-testid="edit-quote-close">
                <X size={18} />
              </button>
            </div>
            <QuotationBuilder
              customer={editingQuote.customer_snapshot}
              existingQuote={editingQuote}
              onClose={() => setEditingQuote(null)}
              onSaved={onEditSaved}
            />
          </div>
        </div>
      )}
    </div>
  );
}
